import React, { useRef } from 'react';
import { useSceneScrollTimings } from '../../hooks/useSceneScrollTimings';
import { DualCTA, CTADetails } from '../shared/DualCTA';
import { Check } from 'lucide-react';

/**
 * Scene 5 - Decision
 * 
 * Final CTA scene with dual buttons and trust badges.
 * Colors: White/light gradient (reverse contrast for clarity)
 */

const trustBadges = [
  { id: 1, text: '500+ businesses helped' },
  { id: 2, text: '$12M savings identified' },
  { id: 3, text: '100% money-back guarantee' },
];

export const Scene5Decision = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);

  // Track scene entry/exit
  useSceneScrollTimings(sectionRef, {
    start: 'top center',
    end: 'bottom center',
    onEnter: () => {
      if (onSceneChange) onSceneChange(5);
    },
    onUpdate: (progress) => {
      if (onProgressUpdate) onProgressUpdate(92 + progress * 8); // 92-100% of journey
    },
  });

  return (
    <section
      id="decision"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-decision overflow-hidden pt-20"
      data-testid="decision-section"
    >
      <div className="max-w-5xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h2
            className="text-4xl sm:text-5xl lg:text-7xl font-display font-semibold text-navy mb-6 leading-tight"
            data-testid="decision-headline"
          >
            Stop leaving money
            <br />
            on the table.
          </h2>
          <p
            className="text-xl sm:text-2xl font-sans text-navy/80 max-w-2xl mx-auto"
            data-testid="decision-subheadline"
          >
            Your next decision starts your next return.
          </p>
        </div>

        {/* Dual CTA */}
        <div className="flex justify-center mb-12">
          <DualCTA
            onStartFree={() => {
              console.log('Start Free clicked');
              alert('Redirecting to strategy explorer...');
            }}
            onGetAnalyzed={() => {
              console.log('Get Analyzed clicked');
              alert('Redirecting to CPA analysis checkout...');
            }}
          />
        </div>

        {/* CTA Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-3xl mx-auto">
          <div data-testid="cta-details-free">
            <h3 className="text-lg font-sans font-semibold text-navy mb-3">
              Start Free includes:
            </h3>
            <CTADetails type="free" />
          </div>
          <div data-testid="cta-details-paid">
            <h3 className="text-lg font-sans font-semibold text-navy mb-3">
              Get Analyzed includes:
            </h3>
            <CTADetails type="paid" />
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-8 mb-12">
          {trustBadges.map((badge) => (
            <div
              key={badge.id}
              className="flex items-center gap-2"
              data-testid={`trust-badge-${badge.id}`}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-400/10">
                <Check className="w-4 h-4 text-teal-500" strokeWidth={3} />
              </div>
              <span className="text-sm font-sans text-navy/70">{badge.text}</span>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs font-sans text-navy/50 max-w-2xl mx-auto">
          Let's Talk Strategy - Professional financial strategy consulting for businesses.
        </p>
      </div>
    </section>
  );
};